/**
 * Formats a date relative to now using natural language:
 * "yesterday", "2 days ago", "3 weeks ago", "2 months ago", etc.
 */
export function formatRelativeDate(date: Date, locale: string = 'en'): string {
   const { value, unit } = findTimeDistance(date);
   return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(value, unit);
}

/**
 * Formats the distance between a date and now as a duration:
 * "today", "3 days", "2 weeks", "8 months", "3 years", etc.
 */
export function formatDuration(date: Date, locale: string = 'en'): string {
   const { value, unit } = findTimeDistance(date);
   const absValue = Math.abs(value);
   if (absValue === 0) {
      return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(0, unit);
   }
   const singularUnit = unit.replace(/s$/, '');
   return new Intl.NumberFormat(locale, {
      style: 'unit',
      unit: singularUnit,
      unitDisplay: 'long',
   }).format(absValue);
}

const DIVISIONS: Array<{ amount: number; name: Intl.RelativeTimeFormatUnit }> = [
   { amount: 7, name: 'days' },
   { amount: 4.345_24, name: 'weeks' },
   { amount: 12, name: 'months' },
   { amount: Number.POSITIVE_INFINITY, name: 'years' },
];

function findTimeDistance(date: Date): { value: number; unit: Intl.RelativeTimeFormatUnit } {
   const normalizedDate = new Date(date);
   normalizedDate.setHours(0, 0, 0, 0);
   const today = new Date();
   today.setHours(0, 0, 0, 0);
   let duration = (normalizedDate.getTime() - today.getTime()) / 1000 / 60 / 60 / 24;

   for (const division of DIVISIONS) {
      if (Math.abs(duration) < division.amount) {
         return { value: Math.round(duration), unit: division.name };
      }
      duration /= division.amount;
   }
   return { value: Math.round(duration), unit: 'years' };
}
